
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Code2,
  Heading2,
  Heading3,
  Info,
  Lightbulb,
  List,
  Minus,
  Plus,
  Table2,
  Trash2,
  Type,
  Workflow,
  CircleAlert,
} from 'lucide-react';
import type { ArticleContent, ContentBlock } from '@/lib/articleContent';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';

type CalloutKind = 'info' | 'tip' | 'warning';

function CalloutBlockVisualEditor({
  block,
  kind,
  index,
  disabled,
  onChange,
}: {
  block: Extract<ContentBlock, { type: CalloutKind }>;
  kind: CalloutKind;
  index: number;
  disabled?: boolean;
  onChange: (b: ContentBlock) => void;
}) {
  const Icon = kind === 'info' ? Info : kind === 'tip' ? Lightbulb : CircleAlert;
  const shell =
    kind === 'info'
      ? {
          wrap: 'border-blue-200 bg-blue-50',
          icon: 'text-blue-600',
          title:
            'text-blue-900 placeholder:text-blue-800/45 focus:ring-blue-400/35 border border-transparent focus:border-blue-200 bg-white/55 focus:bg-white',
        }
      : kind === 'tip'
        ? {
            wrap: 'border-green-200 bg-green-50',
            icon: 'text-green-600',
            title:
              'text-green-900 placeholder:text-green-800/45 focus:ring-green-400/35 border border-transparent focus:border-green-200 bg-white/55 focus:bg-white',
          }
        : {
            wrap: 'border-orange-200 bg-orange-50',
            icon: 'text-orange-600',
            title:
              'text-orange-900 placeholder:text-orange-800/45 focus:ring-orange-400/35 border border-transparent focus:border-orange-200 bg-white/55 focus:bg-white',
          };

  return (
    <div className={cn('rounded-xl border p-5 shadow-sm', shell.wrap)}>
      <div className="flex gap-3">
        <div className="flex-shrink-0 pt-0.5">
          <Icon className={cn('h-5 w-5', shell.icon)} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <input
            type="text"
            value={block.title}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            disabled={disabled}
            aria-label="Título da caixa"
            placeholder={kind === 'info' ? 'Informação' : kind === 'tip' ? 'Dica' : 'Atenção'}
            className={cn(
              'mb-0 w-full rounded-lg px-2 py-1 text-base font-semibold shadow-none transition-colors focus:outline-none focus:ring-2',
              shell.title
            )}
          />
          <div className="text-sm text-current [&_.prose]:max-w-none">
            <RichTextEditor
              value={block.content}
              onChange={(html) => onChange({ ...block, content: html })}
              disabled={disabled}
              resetKey={`callout-${index}-${block.type}`}
              toolbar="extended"
              editorMinHeightClass="min-h-[120px]"
              tone={kind}
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

type GuideBlocksEditorProps = {
  value: ArticleContent;
  onChange: (next: ArticleContent) => void;
  disabled?: boolean;
};

const surface = cn(
  'rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.03] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
);

const toolbarBtn = cn(
  'h-9 gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 font-inter text-xs font-medium text-blue-100',
  'hover:bg-white/10 hover:text-white disabled:opacity-40'
);

function newBlock(type: ContentBlock['type']): ContentBlock {
  switch (type) {
    case 'paragraph':
      return { type: 'paragraph', text: '' };
    case 'heading':
      return { type: 'heading', level: 2, text: '' };
    case 'list':
      return { type: 'list', items: [''] };
    case 'steps':
      return {
        type: 'steps',
        items: [{ title: 'Passo 1', description: '' }],
      };
    case 'info':
      return { type: 'info', title: 'Informação', content: '' };
    case 'tip':
      return { type: 'tip', title: 'Dica', content: '' };
    case 'warning':
      return { type: 'warning', title: 'Aviso', content: '' };
    case 'table':
      return {
        type: 'table',
        headers: ['Coluna A', 'Coluna B'],
        rows: [['', '']],
      };
    case 'divider':
      return { type: 'divider' };
    case 'code':
      return { type: 'code', language: 'HTML', code: '<!-- Exemplo -->\n' };
  }
}

type BlockEditorRowProps = {
  block: ContentBlock;
  index: number;
  total: number;
  disabled?: boolean;
  onChange: (b: ContentBlock) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
};

function BlockEditorRow({
  block,
  index,
  total,
  disabled,
  onChange,
  onRemove,
  onMove,
}: BlockEditorRowProps) {
  const label = (() => {
    switch (block.type) {
      case 'paragraph':
        return 'Parágrafo';
      case 'heading':
        return block.level === 2 ? 'Título (nível 2)' : 'Título (nível 3)';
      case 'list':
        return 'Lista com marcadores';
      case 'steps':
        return 'Passos numerados';
      case 'info':
        return 'Caixa de informação';
      case 'tip':
        return 'Caixa de dica';
      case 'warning':
        return 'Caixa de aviso';
      case 'table':
        return 'Tabela';
      case 'divider':
        return 'Separador visual';
      case 'code':
        return 'Consola (código)';
    }
  })();

  const renderFields = () => {
    switch (block.type) {
      case 'paragraph':
        return (
          <RichTextEditor
            value={block.text}
            onChange={(html) => onChange({ ...block, text: html })}
            disabled={disabled}
            resetKey={`paragraph-${index}`}
            toolbar="extended"
            editorMinHeightClass="min-h-[140px]"
          />
        );
      case 'heading':
        return (
          <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
            <div className="grid gap-1">
              <Label className="text-[11px] text-slate-400">Nível</Label>
              <select
                value={block.level}
                onChange={(e) =>
                  onChange({
                    ...block,
                    level: Number(e.target.value) as 2 | 3,
                  })
                }
                className={painelInputClass}
                disabled={disabled}
              >
                <option value={2}>Título 2</option>
                <option value={3}>Título 3</option>
              </select>
            </div>
            <div className="grid gap-1">
              <Label className="text-[11px] text-slate-400">Texto</Label>
              <input
                value={block.text}
                onChange={(e) => onChange({ ...block, text: e.target.value })}
                className={painelInputClass}
                placeholder="Título"
                disabled={disabled}
              />
            </div>
          </div>
        );
      case 'list':
        return (
          <div className="space-y-2">
            {block.items.map((item, i) => (
              <div key={i} className="space-y-1">
                <RichTextEditor
                  value={item}
                  onChange={(html) => {
                    const items = [...block.items];
                    items[i] = html;
                    onChange({ ...block, items });
                  }}
                  disabled={disabled}
                  resetKey={`list-${index}-${i}`}
                  toolbar="extended"
                  editorMinHeightClass="min-h-[80px]"
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 shrink-0 rounded-lg text-rose-300 hover:bg-rose-500/15"
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        ...block,
                        items: block.items.filter((_, j) => j !== i),
                      })
                    }
                    aria-label="Remover item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={toolbarBtn}
              disabled={disabled}
              onClick={() => onChange({ ...block, items: [...block.items, ''] })}
            >
              <Plus className="h-3.5 w-3.5" />
              Item
            </Button>
          </div>
        );
      case 'steps':
        return (
          <div className="space-y-4">
            {block.items.map((step, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-200/70">
                    Passo {i + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-rose-300 hover:bg-rose-500/15"
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        ...block,
                        items: block.items.filter((_, j) => j !== i),
                      })
                    }
                  >
                    Remover
                  </Button>
                </div>
                <input
                  value={step.title}
                  onChange={(e) => {
                    const items = [...block.items];
                    items[i] = { ...items[i], title: e.target.value };
                    onChange({ ...block, items });
                  }}
                  className={painelInputClass}
                  placeholder="Título do passo"
                  disabled={disabled}
                />
                <RichTextEditor
                  value={step.description}
                  onChange={(html) => {
                    const items = [...block.items];
                    items[i] = { ...items[i], description: html };
                    onChange({ ...block, items });
                  }}
                  disabled={disabled}
                  resetKey={`step-desc-${index}-${i}`}
                  toolbar="extended"
                  editorMinHeightClass="min-h-[100px]"
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={toolbarBtn}
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...block,
                  items: [
                    ...block.items,
                    { title: `Passo ${block.items.length + 1}`, description: '' },
                  ],
                })
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Passo
            </Button>
          </div>
        );
      case 'info':
        return (
          <CalloutBlockVisualEditor
            block={block}
            kind="info"
            index={index}
            disabled={disabled}
            onChange={onChange}
          />
        );
      case 'tip':
        return (
          <CalloutBlockVisualEditor
            block={block}
            kind="tip"
            index={index}
            disabled={disabled}
            onChange={onChange}
          />
        );
      case 'warning':
        return (
          <CalloutBlockVisualEditor
            block={block}
            kind="warning"
            index={index}
            disabled={disabled}
            onChange={onChange}
          />
        );
      case 'table':
        return (
          <div className="space-y-3 overflow-x-auto">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={toolbarBtn}
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...block,
                    headers: [...block.headers, `Coluna ${block.headers.length + 1}`],
                    rows: block.rows.map((r) => [...r, '']),
                  })
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Coluna
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={toolbarBtn}
                disabled={disabled || block.headers.length <= 1}
                onClick={() => {
                  const headers = block.headers.slice(0, -1);
                  onChange({
                    ...block,
                    headers,
                    rows: block.rows.map((r) => r.slice(0, -1)),
                  });
                }}
              >
                − Coluna
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={toolbarBtn}
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...block,
                    rows: [...block.rows, Array(block.headers.length).fill('')],
                  })
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Linha
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={toolbarBtn}
                disabled={disabled || block.rows.length <= 1}
                onClick={() =>
                  onChange({
                    ...block,
                    rows: block.rows.slice(0, -1),
                  })
                }
              >
                − Linha
              </Button>
            </div>
            <table className="w-full min-w-[320px] border-collapse text-xs">
              <thead>
                <tr>
                  {block.headers.map((h, ci) => (
                    <th key={ci} className="p-1 align-top">
                      <input
                        value={h}
                        onChange={(e) => {
                          const headers = [...block.headers];
                          headers[ci] = e.target.value;
                          onChange({ ...block, headers });
                        }}
                        className={painelInputClass}
                        disabled={disabled}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="p-1 align-top">
                        <input
                          value={cell}
                          onChange={(e) => {
                            const rows = block.rows.map((r) => [...r]);
                            rows[ri][ci] = e.target.value;
                            onChange({ ...block, rows });
                          }}
                          className={painelInputClass}
                          disabled={disabled}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'divider':
        return (
          <p className="text-center text-xs text-slate-500">
            Linha horizontal no artigo publicado — sem campos.
          </p>
        );
      case 'code':
        return (
          <div className="grid gap-3 text-left">
            <div className="grid gap-1">
              <Label className="text-[11px] text-slate-400">Etiqueta (ex.: HTML, CSS, PHP)</Label>
              <input
                value={block.language}
                onChange={(e) => onChange({ ...block, language: e.target.value })}
                className={painelInputClass}
                placeholder="Linguagem"
                disabled={disabled}
              />
            </div>
            <div className="grid gap-1">
              <Label className="text-[11px] text-slate-400">Código</Label>
              <textarea
                value={block.code}
                onChange={(e) => onChange({ ...block, code: e.target.value })}
                className={cn(
                  painelTextareaClass,
                  'min-h-[200px] font-mono text-[13px] leading-relaxed text-emerald-100/95',
                  'border-emerald-500/20 bg-[#0a0f14]'
                )}
                placeholder="Cole ou escreva o exemplo…"
                disabled={disabled}
                spellCheck={false}
              />
            </div>
          </div>
        );
    }
  };

  const isCallout =
    block.type === 'info' || block.type === 'tip' || block.type === 'warning';

  return (
    <div
      className={cn(
        isCallout ? 'space-y-3' : 'rounded-xl border border-white/10 bg-white/[0.04] p-4',
        disabled && 'opacity-50'
      )}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-sky-500/20 px-2 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide text-sky-200">
          {label}
        </span>
        <span className="text-[11px] text-slate-500">#{index + 1}</span>
        <div className="ml-auto flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg text-blue-200 hover:bg-white/10"
            disabled={disabled || index <= 0}
            onClick={() => onMove(-1)}
            aria-label="Mover para cima"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg text-blue-200 hover:bg-white/10"
            disabled={disabled || index >= total - 1}
            onClick={() => onMove(1)}
            aria-label="Mover para baixo"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg text-rose-300 hover:bg-rose-500/15"
            disabled={disabled}
            onClick={onRemove}
            aria-label="Remover bloco"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {renderFields()}
    </div>
  );
}

export function GuideBlocksEditor({ value, onChange, disabled }: GuideBlocksEditorProps) {
  const setBlocks = (blocks: ContentBlock[]) => onChange({ ...value, blocks });

  const patchBlock = (i: number, b: ContentBlock) => {
    const blocks = value.blocks.map((x, j) => (j === i ? b : x));
    setBlocks(blocks);
  };

  const add = (type: ContentBlock['type']) => {
    setBlocks([...value.blocks, newBlock(type)]);
  };

  const addHeading = (level: 2 | 3) => {
    setBlocks([...value.blocks, { type: 'heading', level, text: '' }]);
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.blocks.length) return;
    const blocks = [...value.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    setBlocks(blocks);
  };

  const sidebarBtn = cn(
    'h-9 w-full justify-start gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 font-inter text-xs font-medium text-blue-100',
    'hover:bg-white/10 hover:text-white disabled:opacity-40'
  );

  return (
    <div className={cn('space-y-6 text-left', disabled && 'pointer-events-none opacity-50')}>
      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1 space-y-6">
          <div className="mb-1 flex flex-nowrap items-center gap-x-3 gap-y-0 text-left">
            <span className="shrink-0 whitespace-nowrap font-poppins text-sm font-medium text-blue-100/90">
              Introdução do Guia
            </span>
            <span className="hidden min-w-0 truncate text-[11px] text-slate-500 sm:inline">
              Texto inicial com formatação rica (listas, negrito, links, imagens, trechos em consola).
            </span>
          </div>
          <RichTextEditor
            value={value.intro}
            onChange={(html) => onChange({ ...value, intro: html })}
            disabled={disabled}
            resetKey="guide-intro"
            toolbar="extended"
            editorMinHeightClass="min-h-[128px]"
          />

          <div className="space-y-3">
            <Label className="text-blue-100/90">Conteúdo ({value.blocks.length} blocos)</Label>
            {value.blocks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-10 text-center text-sm text-slate-500">
                Utilize a barra à direita para inserir blocos no guia.
              </div>
            ) : (
              value.blocks.map((block, i) => (
                <BlockEditorRow
                  key={i}
                  block={block}
                  index={i}
                  total={value.blocks.length}
                  disabled={disabled}
                  onChange={(b) => patchBlock(i, b)}
                  onRemove={() => setBlocks(value.blocks.filter((_, j) => j !== i))}
                  onMove={(dir) => move(i, dir)}
                />
              ))
            )}
          </div>
        </div>

        <aside
          className={cn(
            surface,
            'w-full shrink-0 space-y-4 p-4 xl:sticky xl:top-20 xl:max-h-[calc(100vh-5rem)] xl:w-72 xl:overflow-y-auto'
          )}
        >
          <div className="border-b border-white/10 pb-3">
            <span className="font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/80">
              Inserir bloco
            </span>
            <p className="mt-1 font-inter text-[11px] text-slate-500">
              Adicione secções ao artigo. Campos de texto incluem a barra de ferramentas completa.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Texto
              </p>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('paragraph')}
                >
                  <Type className="h-3.5 w-3.5 shrink-0" />
                  Parágrafo
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => addHeading(2)}
                >
                  <Heading2 className="h-3.5 w-3.5 shrink-0" />
                  Título 2
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => addHeading(3)}
                >
                  <Heading3 className="h-3.5 w-3.5 shrink-0" />
                  Título 3
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('list')}
                >
                  <List className="h-3.5 w-3.5 shrink-0" />
                  Lista
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Estrutura
              </p>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('steps')}
                >
                  <Workflow className="h-3.5 w-3.5 shrink-0" />
                  Passos
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('table')}
                >
                  <Table2 className="h-3.5 w-3.5 shrink-0" />
                  Tabela
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('code')}
                >
                  <Code2 className="h-3.5 w-3.5 shrink-0" />
                  Consola (código)
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('divider')}
                >
                  <Minus className="h-3.5 w-3.5 shrink-0" />
                  Separador
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Destaques
              </p>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('info')}
                >
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  Info
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('tip')}
                >
                  <Lightbulb className="h-3.5 w-3.5 shrink-0" />
                  Dica
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={sidebarBtn}
                  disabled={disabled}
                  onClick={() => add('warning')}
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Aviso
                </Button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
